import { DEFAULT_WORKSPACE_SCHEDULE_SETTINGS } from "@elaso/shared/types/db";
import { CalendarDays, Hash } from "lucide-react";
import { useMemo } from "react";
import { useStore } from "zustand";
import {
	type UserScheduleSession,
	useFullScheduleSessions,
	useHasFullScheduleAccess,
	useUserScheduleSessions,
} from "@/hooks/use-user-schedule";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

// biome-ignore lint/style/noMagicNumbers: Millisecond conversion constants for day calculations.
const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24;
const TOMORROW_DIFF_DAYS = 1;

function formatTimeRange(startTime: string, endTime: string): string {
	const start = new Date(startTime);
	const end = new Date(endTime);
	const options: Intl.DateTimeFormatOptions = {
		hour: "numeric",
		minute: "2-digit",
	};
	return `${start.toLocaleTimeString(undefined, options)} - ${end.toLocaleTimeString(undefined, options)}`;
}

function getDayLabel(date: Date): string {
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const diffDays = Math.round(
		(target.getTime() - today.getTime()) / ONE_DAY_IN_MS
	);

	if (diffDays === 0) return "Today";
	if (diffDays === TOMORROW_DIFF_DAYS) return "Tomorrow";

	return date.toLocaleDateString(undefined, {
		weekday: "long",
		month: "long",
		day: "numeric",
	});
}

function formatRoleLabel(roleKey: string): string {
	const normalized = roleKey.includes("--")
		? (roleKey.split("--").pop() ?? roleKey)
		: (roleKey.split(":").pop() ?? roleKey);

	return normalized
		.split(/[-_]/g)
		.filter(Boolean)
		.map((token) => token.charAt(0).toUpperCase() + token.slice(1))
		.join(" ");
}

type FullScheduleDayTable = {
	dayLabel: string;
	tableChannels: Array<{ channelId: string; channelName: string }>;
	sessions: UserScheduleSession[];
};

type SessionCellAssignments = Map<
	string,
	{ users: Set<string>; roles: Set<string> }
>;

function buildFullScheduleDayTables({
	fullScheduleSessions,
	sessionChannelUsers,
	sessionChannelRoles,
	channels,
}: {
	fullScheduleSessions: UserScheduleSession[];
	sessionChannelUsers: Array<{ sessionId: string; channelId: string }>;
	sessionChannelRoles: Array<{ sessionId: string; channelId: string }>;
	channels: Map<string, { name: string }>;
}): FullScheduleDayTable[] {
	const sessionsByDay = new Map<string, UserScheduleSession[]>();
	const dayChannels = new Map<string, Set<string>>();
	const sessionDay = new Map<string, string>();

	for (const session of fullScheduleSessions) {
		const dayLabel = getDayLabel(new Date(session.startTime));
		sessionDay.set(session.id, dayLabel);

		const daySessions = sessionsByDay.get(dayLabel);
		if (daySessions) {
			daySessions.push(session);
		} else {
			sessionsByDay.set(dayLabel, [session]);
		}

		if (!dayChannels.has(dayLabel)) {
			dayChannels.set(dayLabel, new Set());
		}

		const channelIds = dayChannels.get(dayLabel);
		for (const channel of session.channels) {
			channelIds?.add(channel.channelId);
		}
	}

	for (const assignment of sessionChannelUsers) {
		const dayLabel = sessionDay.get(assignment.sessionId);
		if (!dayLabel) continue;
		dayChannels.get(dayLabel)?.add(assignment.channelId);
	}

	for (const assignment of sessionChannelRoles) {
		const dayLabel = sessionDay.get(assignment.sessionId);
		if (!dayLabel) continue;
		dayChannels.get(dayLabel)?.add(assignment.channelId);
	}

	return Array.from(sessionsByDay.entries()).map(([dayLabel, daySessions]) => {
		const channelIds = dayChannels.get(dayLabel) ?? new Set<string>();
		const tableChannels = Array.from(channelIds)
			.map((channelId) => ({
				channelId,
				channelName: channels.get(channelId)?.name ?? "Unknown",
			}))
			.sort((a, b) => a.channelName.localeCompare(b.channelName));

		const sortedSessions = [...daySessions].sort(
			(a, b) =>
				new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
		);

		return {
			dayLabel,
			tableChannels,
			sessions: sortedSessions,
		};
	});
}

function buildFullScheduleCellAssignments({
	fullScheduleSessions,
	sessionChannelUsers,
	sessionChannelRoles,
	users,
}: {
	fullScheduleSessions: UserScheduleSession[];
	sessionChannelUsers: Array<{
		sessionId: string;
		channelId: string;
		userId: string;
	}>;
	sessionChannelRoles: Array<{
		sessionId: string;
		channelId: string;
		roleKey: string;
	}>;
	users: Map<
		string,
		{
			profile?: {
				displayName?: string;
				email?: string;
			};
		}
	>;
}): SessionCellAssignments {
	const fullSessionIds = new Set(
		fullScheduleSessions.map((session) => session.id)
	);
	const assignments: SessionCellAssignments = new Map();

	for (const assignment of sessionChannelUsers) {
		if (!fullSessionIds.has(assignment.sessionId)) continue;
		const key = `${assignment.sessionId}:${assignment.channelId}`;
		if (!assignments.has(key)) {
			assignments.set(key, { users: new Set(), roles: new Set() });
		}

		const profile = users.get(assignment.userId)?.profile;
		const label =
			profile?.displayName?.trim() || profile?.email || assignment.userId;

		assignments.get(key)?.users.add(label);
	}

	for (const assignment of sessionChannelRoles) {
		if (!fullSessionIds.has(assignment.sessionId)) continue;
		const key = `${assignment.sessionId}:${assignment.channelId}`;
		if (!assignments.has(key)) {
			assignments.set(key, { users: new Set(), roles: new Set() });
		}

		assignments.get(key)?.roles.add(formatRoleLabel(assignment.roleKey));
	}

	return assignments;
}

export function ScheduleView() {
	const userSessions = useUserScheduleSessions();
	const fullScheduleSessions = useFullScheduleSessions();
	const hasFullScheduleAccess = useHasFullScheduleAccess();
	const sessions = hasFullScheduleAccess ? fullScheduleSessions : userSessions;
	const navigate = useStore(uiStore, (s) => s.navigate);
	const channels = useStore(coreStore, (state) => state.channels);
	const users = useStore(coreStore, (state) => state.users);
	const sessionChannelRoles = useStore(
		coreStore,
		(state) => state.sessionChannelRoles
	);
	const sessionChannelUsers = useStore(
		coreStore,
		(state) => state.sessionChannelUsers
	);
	const workspaces = useStore(coreStore, (state) => state.workspaces);
	const currentWorkspaceId = useStore(
		uiStore,
		(state) => state.currentWorkspaceId
	);
	const currentWorkspace = workspaces.get(currentWorkspaceId || "");
	const scheduleSettings =
		(
			currentWorkspace as {
				scheduleSettings?: { scheduleTitle?: string | null };
			}
		)?.scheduleSettings ?? DEFAULT_WORKSPACE_SCHEDULE_SETTINGS;
	const scheduleTitleText = scheduleSettings.scheduleTitle || "Schedule";

	const groupedSessions = useMemo(() => {
		const groups = new Map<string, typeof sessions>();
		for (const session of sessions) {
			const label = getDayLabel(new Date(session.startTime));
			const existingSessions = groups.get(label);
			if (existingSessions) {
				existingSessions.push(session);
				continue;
			}

			groups.set(label, [session]);
		}
		return groups;
	}, [sessions]);

	const fullScheduleDayTables = useMemo(
		() =>
			buildFullScheduleDayTables({
				fullScheduleSessions,
				sessionChannelUsers,
				sessionChannelRoles,
				channels,
			}),
		[fullScheduleSessions, sessionChannelUsers, sessionChannelRoles, channels]
	);

	const fullScheduleCellAssignments = useMemo(
		() =>
			buildFullScheduleCellAssignments({
				fullScheduleSessions,
				sessionChannelUsers,
				sessionChannelRoles,
				users,
			}),
		[fullScheduleSessions, sessionChannelUsers, sessionChannelRoles, users]
	);

	const handleChannelClick = (channelId: string, e: React.MouseEvent) => {
		navigate(
			{ type: "textChannel", params: { channelId } },
			{ newTab: e.metaKey || e.ctrlKey }
		);
	};

	if (sessions.length === 0) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
				<CalendarDays className="size-10 opacity-50" />
				<p className="text-lg">No upcoming sessions</p>
				<p className="text-sm">
					{hasFullScheduleAccess
						? "There are no visible upcoming sessions in this workspace."
						: "You don't have any scheduled sessions at the moment."}
				</p>
			</div>
		);
	}

	if (hasFullScheduleAccess) {
		return (
			<div className="flex h-full flex-col gap-6 overflow-auto p-6">
				<div className="flex items-center gap-2">
					<CalendarDays className="size-5" />
					<h2 className="font-semibold text-lg">{scheduleTitleText}</h2>
				</div>

				{fullScheduleDayTables.map((day) => (
					<div className="flex flex-col gap-3" key={day.dayLabel}>
						<h3 className="font-medium text-muted-foreground text-sm">
							{day.dayLabel}
						</h3>

						<div className="overflow-x-auto rounded-lg border bg-card">
							<table className="w-full border-collapse">
								<thead>
									<tr className="border-b bg-muted/50 text-left">
										<th className="p-3 font-medium text-sm">Session</th>
										<th className="p-3 font-medium text-sm">Time</th>
										{day.tableChannels.map((channel) => (
											<th
												className="p-3 font-medium text-sm"
												key={channel.channelId}
											>
												<button
													className="inline-flex items-center gap-1 text-left hover:text-foreground"
													onClick={(e) =>
														handleChannelClick(channel.channelId, e)
													}
													type="button"
												>
													<Hash className="size-3" />
													{channel.channelName}
												</button>
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{day.sessions.map((session) => (
										<tr
											className="border-b align-top last:border-b-0"
											key={session.id}
										>
											<td className="p-3 font-medium text-sm">
												{session.name}
											</td>
											<td className="p-3 text-muted-foreground text-sm">
												{formatTimeRange(session.startTime, session.endTime)}
											</td>
											{day.tableChannels.map((channel) => {
												const assignment = fullScheduleCellAssignments.get(
													`${session.id}:${channel.channelId}`
												);
												if (!assignment) {
													return (
														<td
															className="p-3 text-muted-foreground text-sm"
															key={channel.channelId}
														>
															-
														</td>
													);
												}

												const userLabels = Array.from(assignment.users).sort();
												const roleLabels = Array.from(assignment.roles).sort();

												return (
													<td className="p-3" key={channel.channelId}>
														<div className="flex flex-col gap-1">
															{userLabels.map((label) => (
																<div
																	className="rounded bg-accent/40 px-2 py-1 text-xs"
																	key={`user-${label}`}
																>
																	{label}
																</div>
															))}
															{roleLabels.map((label) => (
																<div
																	className="rounded px-2 py-1 text-xs"
																	key={`role-${label}`}
																>
																	{label}
																</div>
															))}
														</div>
													</td>
												);
											})}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col gap-6 overflow-auto p-6">
			<div className="flex items-center gap-2">
				<CalendarDays className="size-5" />
				<h2 className="font-semibold text-lg">{scheduleTitleText}</h2>
			</div>

			{Array.from(groupedSessions.entries()).map(([dayLabel, daySessions]) => (
				<div className="flex flex-col gap-3" key={dayLabel}>
					<h3 className="font-medium text-muted-foreground text-sm">
						{dayLabel}
					</h3>
					{daySessions.map((session) => {
						const firstChannel = session.channels[0];

						if (firstChannel) {
							return (
								<button
									className="flex w-full cursor-pointer items-center justify-between rounded-lg border bg-card p-4 text-left transition-colors hover:bg-accent/50"
									key={session.id}
									onClick={(e) => handleChannelClick(firstChannel.channelId, e)}
									type="button"
								>
									<div className="flex items-center gap-2">
										<span className="font-medium">{session.name}</span>
										<span className="flex items-center gap-1 text-muted-foreground text-sm">
											<Hash className="size-3" />
											{firstChannel.channelName}
										</span>
									</div>
									<span className="text-muted-foreground text-sm">
										{formatTimeRange(session.startTime, session.endTime)}
									</span>
								</button>
							);
						}

						return (
							<div
								className="flex items-center justify-between rounded-lg border bg-card p-4"
								key={session.id}
							>
								<span className="font-medium">{session.name}</span>
								<span className="text-muted-foreground text-sm">
									{formatTimeRange(session.startTime, session.endTime)}
								</span>
							</div>
						);
					})}
				</div>
			))}
		</div>
	);
}
